import type { ProjectOffering } from '../types';
import { parseOfferingAutoTitleIndex } from './offeringLabels';

/** Offering with the newest `last_used_at` in a project list. */
export function resolveLastUsedOfferingId(offerings: ProjectOffering[]): string | null {
  let bestId: string | null = null;
  let bestTime = -Infinity;
  for (const o of offerings) {
    if (!o.last_used_at) continue;
    const t = new Date(o.last_used_at).getTime();
    if (!Number.isNaN(t) && t > bestTime) {
      bestTime = t;
      bestId = o.id;
    }
  }
  return bestId;
}

/** Which offering chain the Angebot tab should show: last used, else primary, else first. */
export function pickAngebotTabOfferingId(lead: {
  primary_offering_id?: string | null;
  offerings?: ProjectOffering[];
} | null): string | null {
  if (!lead) return null;
  const lastUsed = resolveLastUsedOfferingId(lead.offerings ?? []);
  return lastUsed ?? lead.primary_offering_id ?? lead.offerings?.[0]?.id ?? null;
}

/** Laufnummer aus „Vorgang N“ / „Angebot N“ oder der letzten Zifferngruppe der display_id (z. B. ANG-26-00003 → 3). */
function offeringSequenceNumber(o: ProjectOffering): number | null {
  const fromTitle = parseOfferingAutoTitleIndex(o.title);
  if (fromTitle != null) return fromTitle;
  const did = (o.display_id || '').trim();
  if (did) {
    const last = did.split(/[-/.]/).pop() ?? '';
    const n = parseInt(last, 10);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Reihenfolge: Vorgang 1, 2, 3, … (danach Erstellungsdatum). */
export function sortOfferingsForDisplay(
  offerings: ProjectOffering[],
  _primaryOfferingId?: string | null,
): ProjectOffering[] {
  return [...offerings].sort((a, b) => {
    const aSeq = offeringSequenceNumber(a);
    const bSeq = offeringSequenceNumber(b);
    if (aSeq != null && bSeq != null && aSeq !== bSeq) return aSeq - bSeq;
    if (aSeq != null && bSeq == null) return -1;
    if (aSeq == null && bSeq != null) return 1;
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
    if (aTime !== bTime) return aTime - bTime;
    return (a.display_id || a.id).localeCompare(b.display_id || b.id, 'de');
  });
}
